import React, { useState, useEffect, Fragment } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import './ShopifyOrderList.css';

const ShopifyOrderList = () => {
  const navigate = useNavigate();
  const { store } = useParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [checkingNetSuite, setCheckingNetSuite] = useState(false);
  const [updatingFulfillment, setUpdatingFulfillment] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [selectedOrders, setSelectedOrders] = useState(new Set());
  const [importing, setImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [testMode, setTestMode] = useState(false);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    financialStatus: '',
    fulfillmentStatus: 'null', // Default to unfulfilled only
    netsuiteStatus: '', // Show all orders by default
    importStatus: '' // Show all import statuses by default
  });
  const [syncStatus, setSyncStatus] = useState(null);
  const [netSuiteStats, setNetSuiteStats] = useState(null);
  const [pagination, setPagination] = useState({
    limit: 50,
    offset: 0,
    total: 0
  });
  const [orderShipments, setOrderShipments] = useState({});
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');

  // Automation state
  const [automationEnabled, setAutomationEnabled] = useState(false);
  const [automationMetrics, setAutomationMetrics] = useState(null);
  const [togglingAutomation, setTogglingAutomation] = useState(false);

  // Sync logs state
  const [showSyncLogs, setShowSyncLogs] = useState(false);
  const [syncLogs, setSyncLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);

  // Run All state
  const [runningAll, setRunningAll] = useState(false);
  const [runAllProgress, setRunAllProgress] = useState('');

  // Store tab state
  const [activeStore, setActiveStore] = useState(store || 'rewined');

  // Handle store changes from URL
  useEffect(() => {
    if (store && store !== activeStore) {
      setActiveStore(store);
    }
  }, [store]);

  // Fetch data when filters, pagination, sort, or store changes
  useEffect(() => {
    fetchOrders();
    fetchSyncStatus();
    fetchNetSuiteStats();
    fetchAutomationStatus();
    fetchAutomationMetrics();
  }, [filters, pagination.offset, sortBy, sortOrder, activeStore]);

  const fetchOrders = async () => {
    try {
      setLoading(true);

      // Determine API endpoint based on store
      const isFaire = activeStore === 'faire';
      const endpoint = isFaire ? '/api/faire/orders' : '/api/shopify/orders';

      const params = {
        ...filters,
        search: searchTerm,
        limit: pagination.limit,
        offset: pagination.offset,
        sortBy: sortBy,
        sortOrder: sortOrder,
        store: isFaire ? undefined : activeStore // Only send store for Shopify
      };

      const response = await axios.get(endpoint, { params });
      console.log('API Response:', response.data); // Debug log
      setOrders(response.data.orders || []);
      setPagination(prev => ({ ...prev, total: response.data.total || 0 }));
    } catch (error) {
      console.error('Error fetching orders:', error);
      alert('Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  };

  const fetchSyncStatus = async () => {
    try {
      const response = await axios.get('/api/shopify/sync-status');
      setSyncStatus(response.data);
    } catch (error) {
      console.error('Error fetching sync status:', error);
    }
  };

  const fetchNetSuiteStats = async () => {
    try {
      const response = await axios.get('/api/shopify/netsuite-stats');
      setNetSuiteStats(response.data);
    } catch (error) {
      console.error('Error fetching NetSuite stats:', error);
    }
  };

  const fetchAutomationStatus = async () => {
    try {
      const response = await axios.get('/api/shopify/automation/status');
      setAutomationEnabled(response.data.enabled || false);
    } catch (error) {
      console.error('Error fetching automation status:', error);
    }
  };

  const fetchAutomationMetrics = async () => {
    try {
      const response = await axios.get('/api/shopify/automation/metrics');
      setAutomationMetrics(response.data);
    } catch (error) {
      console.error('Error fetching automation metrics:', error);
    }
  };

  const fetchSyncLogs = async () => {
    try {
      setLoadingLogs(true);
      const response = await axios.get('/api/shopify/automation/logs', {
        params: { limit: 20 }
      });
      setSyncLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching sync logs:', error);
    } finally {
      setLoadingLogs(false);
    }
  };

  const formatLogDate = (dateString) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  const formatDuration = (ms) => {
    if (!ms) return '';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const getLogStatusColor = (status) => {
    switch (status) {
      case 'success': return '#28a745';
      case 'failed': return '#dc3545';
      case 'running': return '#007bff';
      default: return '#6c757d';
    }
  };

  const handleToggleAutomation = async () => {
    const newState = !automationEnabled;
    const confirmMessage = newState
      ? 'Enable automation? The system will automatically sync orders, import to NetSuite, and push fulfillments.'
      : 'Disable automation? Automatic processing will stop (manual operations still available).';

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      setTogglingAutomation(true);
      const response = await axios.post('/api/shopify/automation/toggle', {
        enabled: newState
      });

      if (response.data.success) {
        setAutomationEnabled(newState);
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Error toggling automation:', error);
      alert('Failed to toggle automation: ' + (error.response?.data?.error || error.message));
    } finally {
      setTogglingAutomation(false);
    }
  };

  const fetchOrderShipments = async (orderId) => {
    if (orderShipments[orderId]) {
      return; // Already fetched
    }

    try {
      const response = await axios.get(`/api/shopify/order/${orderId}/shipments`);
      setOrderShipments(prev => ({
        ...prev,
        [orderId]: response.data.shipments || []
      }));
    } catch (error) {
      console.error('Error fetching shipments:', error);
    }
  };

  const handleResetOrder = async (order) => {
    if (!window.confirm(`Are you sure you want to reset order #${order.order_number}?\n\nThis will:\n- Clear NetSuite order number\n- Reset import status to pending\n- Delete shipment records\n\nThe order will need to be re-imported to NetSuite.`)) {
      return;
    }

    try {
      const response = await axios.post(`/api/shopify/order/${order.shopify_order_id}/reset`);

      if (response.data.success) {
        alert(`Order #${order.order_number} reset successfully!`);
        await fetchOrders();
      }
    } catch (error) {
      console.error('Error resetting order:', error);
      alert('Failed to reset order: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle sort order if clicking same column
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      // New column, default to descending
      setSortBy(column);
      setSortOrder('desc');
    }
    // Reset to first page when sorting
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return ' ⇅';
    return sortOrder === 'asc' ? ' ↑' : ' ↓';
  };

  const handleRunAll = async () => {
    const isFaire = activeStore === 'faire';

    const confirmMsg = isFaire
      ? 'Run full Faire sync cycle?\n\n1. Sync orders from Faire\n2. Import to NetSuite\n3. Sync fulfillments from NetSuite\n4. Update fulfillment records\n\nThis may take a few minutes.'
      : 'Run full sync cycle?\n\n1. Sync orders from Shopify\n2. Import to NetSuite\n3. Sync fulfillments from NetSuite\n4. Update Shopify fulfillment status\n\nThis may take several minutes.';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      setRunningAll(true);

      if (isFaire) {
        // ========== FAIRE SYNC CYCLE (4 steps) ==========

        // Step 1: Sync Orders from Faire
        setRunAllProgress('Step 1/4: Syncing orders from Faire...');
        const syncResponse = await axios.post('/api/faire/sync', {
          startDate: '2025-11-20'
        });
        console.log(`✓ Synced ${syncResponse.data.ordersCount} Faire orders`);

        // Step 2: Import to NetSuite
        setRunAllProgress('Step 2/4: Importing to NetSuite...');
        // Get all pending Faire orders and import them
        const ordersResponse = await axios.get('/api/faire/orders?importStatus=pending&limit=50');
        const pendingOrders = ordersResponse.data.orders || [];

        if (pendingOrders.length > 0) {
          const importResponse = await axios.post('/api/faire/import', {
            orderIds: pendingOrders.map(o => o.id).slice(0, 20), // Import up to 20 at a time
            testMode: false
          });
          console.log(`✓ Imported ${importResponse.data.successful} Faire orders`);
        }

        // Step 3: Sync Fulfillments from NetSuite
        setRunAllProgress('Step 3/4: Syncing fulfillments from NetSuite...');
        const netsuiteFullfillmentResponse = await axios.post('/api/shopify/sync-fulfillments', {
          storeDomain: 'faire'
        });
        console.log(`✓ Synced ${netsuiteFullfillmentResponse.data.ordersSynced} fulfillments from NetSuite`);

        // Step 4: Update database (Faire doesn't have push-back API like Shopify)
        setRunAllProgress('Step 4/4: Updating fulfillment records...');
        // For Faire, we just update the database - no push back to Faire API
        console.log(`✓ Fulfillment records updated in database`);

        setRunAllProgress('');
        alert(`Faire sync completed!\n\n✓ Synced: ${syncResponse.data.ordersCount} orders\n✓ Imported: ${pendingOrders.length > 0 ? 'orders' : 'no pending orders'}\n✓ Fulfillments: ${netsuiteFullfillmentResponse.data.ordersSynced} orders`);

      } else {
        // ========== SHOPIFY SYNC CYCLE (4 steps) ==========

        // Step 1: Sync Orders
        setRunAllProgress('Step 1/4: Syncing orders from Shopify...');
        const syncResponse = await axios.post('/api/shopify/sync', {
          startDate: store === 'candlefish' ? '2025-11-17' : '2025-10-10',
          store: store || 'rewined'
        });
        console.log(`✓ Synced ${syncResponse.data.ordersCount} orders`);

        // Step 2: Import to NetSuite
        setRunAllProgress('Step 2/4: Importing to NetSuite...');
        const importResponse = await axios.post('/api/shopify/import-orders-bulk', {
          store: store || 'rewined'
        });
        console.log(`✓ Imported ${importResponse.data.successful} orders`);

        // Step 3: Sync Fulfillments from NetSuite
        setRunAllProgress('Step 3/4: Syncing fulfillments from NetSuite...');
        // Map store name to actual store_domain in database
        const storeDomainMap = {
          'rewined': 'rewined-2022.myshopify.com',
          'candlefish': 'candlefish1.myshopify.com'
        };
        const netsuiteFullfillmentResponse = await axios.post('/api/shopify/sync-fulfillments', {
          storeDomain: storeDomainMap[store]
        });
        console.log(`✓ Synced ${netsuiteFullfillmentResponse.data.ordersSynced} fulfillments from NetSuite`);

        // Step 4: Update Shopify Fulfillment
        setRunAllProgress('Step 4/4: Updating Shopify fulfillment status...');
        const fulfillmentResponse = await axios.post('/api/shopify/update-fulfillment');
        console.log(`✓ Updated ${fulfillmentResponse.data.fulfilled || 0} Shopify fulfillments`);

        setRunAllProgress('');
        alert(`Full sync completed!\n\n✓ Synced: ${syncResponse.data.ordersCount} orders\n✓ Imported: ${importResponse.data.successful} orders\n✓ NetSuite fulfillments: ${netsuiteFullfillmentResponse.data.ordersSynced} orders\n✓ Shopify fulfillments: ${fulfillmentResponse.data.fulfilled || 0} orders`);
      }

      // Refresh data
      await fetchOrders();
      await fetchSyncStatus();
      await fetchNetSuiteStats();
      if (!isFaire) {
        await fetchSyncLogs();
      }

    } catch (error) {
      console.error('Error running full sync:', error);
      alert(`Full sync failed at: ${runAllProgress}\n\nError: ${error.response?.data?.error || error.message}`);
    } finally {
      setRunningAll(false);
      setRunAllProgress('');
    }
  };

  const handleSync = async () => {
    const isFaire = activeStore === 'faire';
    const confirmMsg = isFaire
      ? 'Are you sure you want to sync orders from Faire? This may take a few moments.'
      : 'Are you sure you want to sync orders from Shopify? This may take a few moments.';

    if (!window.confirm(confirmMsg)) {
      return;
    }

    try {
      setSyncing(true);

      if (isFaire) {
        const response = await axios.post('/api/faire/sync', {
          startDate: '2025-11-20'
        });
        alert(`Sync completed successfully! ${response.data.ordersCount} orders synced (${response.data.newOrders} new).`);
      } else {
        const response = await axios.post('/api/shopify/sync', {
          startDate: store === 'candlefish' ? '2025-11-17' : '2025-10-10',
          store: store || 'rewined'
        });
        alert(`Sync completed successfully! ${response.data.ordersCount} orders synced.`);
      }

      await fetchOrders();
      await fetchSyncStatus();
    } catch (error) {
      console.error('Error syncing orders:', error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      console.error('Error details from server:', error.response?.data);
      alert('Failed to sync orders: ' + errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  const handleUpdateFulfillment = async () => {
    if (!window.confirm('Update fulfillment statuses from Shopify for orders from the last 30 days?')) {
      return;
    }

    try {
      setUpdatingFulfillment(true);
      const response = await axios.post('/api/shopify/update-fulfillment', {
        daysBack: 30
      }, {
        timeout: 120000  // 2 minute timeout
      });

      const message = `Fulfillment update completed!\nOrders checked: ${response.data.ordersChecked}\nUpdated: ${response.data.updated}\nUnchanged: ${response.data.unchanged}${response.data.errors > 0 ? `\nErrors: ${response.data.errors}` : ''}`;

      alert(message);
      await fetchOrders(); // Refresh to show updated statuses
    } catch (error) {
      console.error('Error updating fulfillment statuses:', error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      alert('Failed to update fulfillment statuses: ' + errorMessage);
    } finally {
      setUpdatingFulfillment(false);
    }
  };

  const handleUpdateDatabaseFulfillment = async () => {
    if (!window.confirm('Update fulfillment statuses for orders currently in the database?\n\nThis will only check the existing orders, not fetch new ones from Shopify.')) {
      return;
    }

    try {
      setUpdatingFulfillment(true);
      const response = await axios.post('/api/shopify/update-database-fulfillment', {}, {
        timeout: 300000  // 5 minute timeout (checking 238+ orders individually)
      });

      const message = `Database fulfillment update completed!\nOrders checked: ${response.data.totalChecked}\nUpdated: ${response.data.updated}\nUnchanged: ${response.data.unchanged}${response.data.notFound > 0 ? `\nNot found in Shopify: ${response.data.notFound}` : ''}${response.data.errors > 0 ? `\nErrors: ${response.data.errors}` : ''}`;

      alert(message);
      await fetchOrders(); // Refresh to show updated statuses
    } catch (error) {
      console.error('Error updating database fulfillment statuses:', error);
      const errorMessage = error.response?.data?.details || error.response?.data?.error || error.message;
      alert('Failed to update fulfillment statuses: ' + errorMessage);
    } finally {
      setUpdatingFulfillment(false);
    }
  };

  const handleCheckNetSuite = async () => {
    try {
      setCheckingNetSuite(true);

      // Use smaller batch size to avoid timeouts
      const response = await axios.post('/api/shopify/batch-check-netsuite', {
        batchSize: 100
      }, {
        timeout: 60000  // 60 second timeout
      });

      const message = response.data.message ||
        `NetSuite check completed!\nChecked: ${response.data.checked} orders\nSynced: ${response.data.synced}\nNot Synced: ${response.data.notSynced}`;

      if (response.data.totalUnchecked > 0) {
        alert(`${message}\n\n${response.data.totalUnchecked} orders still need to be checked.\nClick 'Check NetSuite' again to continue processing.`);
      } else {
        alert(message);
      }

      await fetchOrders(); // Refresh to show updated statuses
      await fetchNetSuiteStats(); // Refresh NetSuite statistics
    } catch (error) {
      console.error('Error checking NetSuite:', error);
      if (error.code === 'ECONNABORTED') {
        alert('Request timed out. Please try with a smaller batch or check server logs.');
      } else {
        alert('Failed to check NetSuite status: ' + (error.response?.data?.error || error.message));
      }
    } finally {
      setCheckingNetSuite(false);
    }
  };

  const handleImportNetSuite = async () => {
    try {
      setCheckingNetSuite(true);

      if (!window.confirm('This will import all pending NetSuite orders from the Rewined subsidiary and match them with Shopify orders. Continue?')) {
        return;
      }

      const response = await axios.post('/api/shopify/import-netsuite-orders', {}, {
        timeout: 120000  // 2 minute timeout for import
      });

      alert(response.data.message || `Import completed! Imported ${response.data.imported} orders and matched ${response.data.matched} with Shopify.`);

      await fetchOrders(); // Refresh to show updated statuses
      await fetchNetSuiteStats(); // Refresh NetSuite statistics
    } catch (error) {
      console.error('Error importing NetSuite orders:', error);
      alert('Failed to import NetSuite orders: ' + (error.response?.data?.error || error.message));
    } finally {
      setCheckingNetSuite(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, offset: 0 }));
    fetchOrders();
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPagination(prev => ({ ...prev, offset: 0 }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const getStatusBadge = (status, type, order = null) => {
    const statusClasses = {
      financial: {
        paid: 'badge-success',
        pending: 'badge-warning',
        refunded: 'badge-danger',
        partially_paid: 'badge-info',
        partially_refunded: 'badge-info'
      },
      fulfillment: {
        fulfilled: 'badge-success',
        partial: 'badge-warning',
        null: 'badge-secondary'
      }
    };

    // Show workshop badge for workshop orders
    if (type === 'fulfillment' && order?.is_workshop) {
      return <span className="badge badge-info" title="Workshop order - no physical shipment required">Workshop</span>;
    }

    const className = type === 'financial'
      ? statusClasses.financial[status] || 'badge-secondary'
      : statusClasses.fulfillment[status] || 'badge-secondary';

    return <span className={`badge ${className}`}>{status || 'unfulfilled'}</span>;
  };

  const getNetSuiteBadge = (order) => {
    const status = order.netsuite_sync_status;

    if (status === 'synced') {
      return (
        <span className="badge badge-netsuite-synced" title={`NetSuite ID: ${order.netsuite_order_id || 'N/A'}`}>
          ✓ In NetSuite
        </span>
      );
    } else if (status === 'not_synced') {
      return (
        <span className="badge badge-netsuite-missing">
          ✗ Not in NetSuite
        </span>
      );
    } else {
      return (
        <span className="badge badge-netsuite-unchecked">
          ? Unchecked
        </span>
      );
    }
  };

  const handlePageChange = (direction) => {
    if (direction === 'next' && pagination.offset + pagination.limit < pagination.total) {
      setPagination(prev => ({ ...prev, offset: prev.offset + prev.limit }));
    } else if (direction === 'prev' && pagination.offset > 0) {
      setPagination(prev => ({ ...prev, offset: Math.max(0, prev.offset - prev.limit) }));
    }
  };

  const toggleOrderExpansion = (orderId) => {
    setExpandedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(orderId)) {
        newSet.delete(orderId);
      } else {
        newSet.add(orderId);
        // Fetch shipments when expanding an order
        const order = orders.find(o => o.id === orderId);
        if (order && order.shopify_order_id) {
          fetchOrderShipments(order.shopify_order_id);
        }
      }
      return newSet;
    });
  };

  const parseLineItems = (lineItems) => {
    if (!lineItems) return [];
    try {
      return typeof lineItems === 'string' ? JSON.parse(lineItems) : lineItems;
    } catch (e) {
      console.error('Error parsing line items:', e);
      return [];
    }
  };

  const parseAddress = (address) => {
    if (!address) return null;
    try {
      return typeof address === 'string' ? JSON.parse(address) : address;
    } catch (e) {
      console.error('Error parsing address:', e);
      return null;
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const eligibleOrderIds = orders
        .filter(order => {
          const notInNetSuite = !order.netsuite_sync_status ||
                               order.netsuite_sync_status === 'not_synced' ||
                               order.netsuite_sync_status === 'pending';
          return notInNetSuite && order.financial_status === 'paid';
        })
        .map(order => order.shopify_order_id);
      setSelectedOrders(new Set(eligibleOrderIds));
    } else {
      setSelectedOrders(new Set());
    }
  };

  const handleSelectOrder = (shopifyOrderId, isEligible) => {
    if (!isEligible) return;

    setSelectedOrders(prev => {
      const newSet = new Set(prev);
      if (newSet.has(shopifyOrderId)) {
        newSet.delete(shopifyOrderId);
      } else {
        newSet.add(shopifyOrderId);
      }
      return newSet;
    });
  };

  const handleImportToNetSuite = async () => {
    if (selectedOrders.size === 0) {
      alert('Please select at least one order to import');
      return;
    }

    if (!window.confirm(`Import ${selectedOrders.size} order(s) to NetSuite?`)) {
      return;
    }

    const isFaire = activeStore === 'faire';

    try {
      setImporting(true);
      setImportProgress({ processed: 0, total: selectedOrders.size, results: [] });

      const endpoint = isFaire ? '/api/faire/import' : '/api/shopify/import-orders-bulk';
      const payload = isFaire
        ? { orderIds: Array.from(selectedOrders), testMode: false }
        : { orderIds: Array.from(selectedOrders), testMode: testMode };

      const response = await axios.post(endpoint, payload);

      setImportProgress(null);

      const { successful, failed, alreadyImported } = response.data;
      alert(`Import Complete!\n\nSuccessful: ${successful}\nFailed: ${failed}\nAlready Imported: ${alreadyImported}`);

      // Clear selection and refresh
      setSelectedOrders(new Set());
      await fetchOrders();
      await fetchNetSuiteStats();
    } catch (error) {
      console.error('Error importing orders:', error);
      alert('Failed to import orders: ' + (error.response?.data?.error || error.message));
      setImportProgress(null);
    } finally {
      setImporting(false);
    }
  };

  // Handle store tab change
  const handleStoreChange = (newStore) => {
    setActiveStore(newStore);
    if (newStore === 'faire') {
      navigate(`/marketplace/faire/orders`);
    } else {
      navigate(`/shopify/${newStore}/orders`);
    }
  };

  const currentPage = Math.floor(pagination.offset / pagination.limit) + 1;
  const totalPages = Math.ceil(pagination.total / pagination.limit);

  const eligibleOrders = orders.filter(order => {
    const notInNetSuite = !order.netsuite_sync_status ||
                         order.netsuite_sync_status === 'not_synced' ||
                         order.netsuite_sync_status === 'pending';
    return notInNetSuite && order.financial_status === 'paid';
  });
  const allEligibleSelected = eligibleOrders.length > 0 &&
    eligibleOrders.every(order => selectedOrders.has(order.shopify_order_id));

  return (
    <div className="shopify-orders-container">
      <div className="shopify-orders-header">
        <div className="header-content">
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <h1>Shopify Orders</h1>
          </div>
          <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={handleRunAll}
              disabled={runningAll}
              className="btn btn-primary"
            >
              {runningAll ? runAllProgress || 'Running...' : 'Run Full Sync'}
            </button>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              padding: '6px 12px',
              background: '#f8f9fa',
              borderRadius: '4px',
              border: '1px solid #dee2e6'
            }}>
              <span style={{ fontSize: '13px', color: '#495057', fontWeight: '500' }}>AUTO</span>
              <label style={{
                position: 'relative',
                display: 'inline-block',
                width: '44px',
                height: '24px',
                margin: 0
              }}>
                <input
                  type="checkbox"
                  checked={automationEnabled}
                  onChange={handleToggleAutomation}
                  disabled={togglingAutomation}
                  style={{ opacity: 0, width: 0, height: 0 }}
                />
                <span style={{
                  position: 'absolute',
                  cursor: 'pointer',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: automationEnabled ? '#007bff' : '#ccc',
                  transition: '0.3s',
                  borderRadius: '24px',
                  pointerEvents: togglingAutomation ? 'none' : 'auto'
                }}>
                  <span style={{
                    position: 'absolute',
                    content: '',
                    height: '18px',
                    width: '18px',
                    left: automationEnabled ? '23px' : '3px',
                    bottom: '3px',
                    backgroundColor: 'white',
                    transition: '0.3s',
                    borderRadius: '50%'
                  }}></span>
                </span>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Sync Logs Section */}
      <div className="filters-section" style={{ marginBottom: '20px', background: '#f5f5f5' }}>
        <button
          onClick={() => {
            setShowSyncLogs(!showSyncLogs);
            if (!showSyncLogs && syncLogs.length === 0) {
              fetchSyncLogs();
            }
          }}
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
            fontWeight: 'bold',
            padding: '10px 0',
            width: '100%',
            textAlign: 'left',
            display: 'flex',
            alignItems: 'center',
            gap: '10px'
          }}
        >
          <span>{showSyncLogs ? '▼' : '▶'}</span>
          <span>Sync Logs</span>
          {!showSyncLogs && syncLogs.length > 0 && (
            <span style={{ fontSize: '14px', color: '#666' }}>({syncLogs.length})</span>
          )}
        </button>

        {showSyncLogs && (
          <div style={{ marginTop: '15px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
              <span style={{ fontSize: '14px', color: '#666' }}>Last 20 operations</span>
              <button
                onClick={fetchSyncLogs}
                disabled={loadingLogs}
                className="btn btn-secondary"
                style={{ padding: '4px 12px', fontSize: '13px' }}
              >
                {loadingLogs ? 'Loading...' : 'Refresh'}
              </button>
            </div>

            {syncLogs.length === 0 ? (
              <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                No sync logs available yet
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflow: 'auto' }}>
                {syncLogs.map((log, idx) => (
                  <div
                    key={idx}
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      background: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '13px'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
                      <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                        <span style={{ color: '#666' }}>{formatLogDate(log.started_at)}</span>
                        <span
                          style={{
                            color: getLogStatusColor(log.status),
                            fontWeight: 'bold',
                            textTransform: 'uppercase'
                          }}
                        >
                          {log.status}
                        </span>
                        <span style={{ fontWeight: '600' }}>{log.operation}</span>
                      </div>
                      {log.duration_ms && (
                        <span style={{ color: '#666' }}>{formatDuration(log.duration_ms)}</span>
                      )}
                    </div>

                    {log.details && log.details.orders_processed !== undefined && (
                      <div style={{ color: '#555', marginTop: '4px' }}>
                        <span>
                          Processed: {log.details.orders_processed || 0} |{' '}
                          Success: {log.details.orders_succeeded || 0} |{' '}
                          Failed: {log.details.orders_failed || 0}
                        </span>
                      </div>
                    )}

                    {log.error_message && (
                      <div style={{ color: '#dc3545', marginTop: '6px', fontSize: '12px' }}>
                        Error: {log.error_message}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="filters-section">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by order #, email, or customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="btn btn-search">Search</button>
        </form>

        <div className="filter-controls">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>Payment Status</label>
            <select
              value={filters.financialStatus}
              onChange={(e) => handleFilterChange('financialStatus', e.target.value)}
            >
              <option value="">All</option>
              <option value="paid">Paid</option>
              <option value="pending">Pending</option>
              <option value="refunded">Refunded</option>
              <option value="partially_paid">Partially Paid</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Fulfillment Status</label>
            <select
              value={filters.fulfillmentStatus}
              onChange={(e) => handleFilterChange('fulfillmentStatus', e.target.value)}
            >
              <option value="all">All</option>
              <option value="null">Unfulfilled</option>
              <option value="fulfilled">Fulfilled</option>
              <option value="partial">Partial</option>
            </select>
          </div>

          <div className="filter-group">
            <label>NetSuite Status</label>
            <select
              value={filters.netsuiteStatus}
              onChange={(e) => handleFilterChange('netsuiteStatus', e.target.value)}
            >
              <option value="">All</option>
              <option value="not_synced">Not in NetSuite</option>
              <option value="synced">In NetSuite</option>
              <option value="unchecked">Unchecked</option>
            </select>
          </div>

          <div className="filter-group">
            <label>Import Status</label>
            <select
              value={filters.importStatus}
              onChange={(e) => handleFilterChange('importStatus', e.target.value)}
            >
              <option value="">All</option>
              <option value="pending">Pending Import</option>
              <option value="imported">Successfully Imported</option>
              <option value="failed">Failed Import</option>
            </select>
          </div>
        </div>
      </div>

      {/* Store Tabs */}
      <div className="store-tabs-navigation">
        <button
          className={`store-tab-button ${activeStore === 'rewined' ? 'active' : ''}`}
          onClick={() => handleStoreChange('rewined')}
        >
          Shopify Rewined
        </button>
        <button
          className={`store-tab-button ${activeStore === 'candlefish' ? 'active' : ''}`}
          onClick={() => handleStoreChange('candlefish')}
        >
          Shopify Candlefish
        </button>
        <button
          className={`store-tab-button ${activeStore === 'faire' ? 'active' : ''}`}
          onClick={() => handleStoreChange('faire')}
        >
          Faire - Rewined
        </button>
      </div>

      {selectedOrders.size > 0 && (
        <div className="bulk-action-bar" style={{
          background: testMode ? '#fff3cd' : '#e8f5e9',
          padding: '15px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
            <div>
              <strong>{selectedOrders.size}</strong> order(s) selected
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={testMode}
                onChange={(e) => setTestMode(e.target.checked)}
              />
              <span style={{ fontWeight: testMode ? 'bold' : 'normal', color: testMode ? '#856404' : 'inherit' }}>
                Test Mode {testMode && '⚠️'}
              </span>
            </label>
          </div>
          <button
            onClick={handleImportToNetSuite}
            disabled={importing}
            className="btn btn-primary"
            style={{ background: testMode ? '#ffc107' : '#28a745', color: testMode ? '#000' : '#fff' }}
          >
            {importing ? `Importing... ${importProgress?.processed || 0}/${importProgress?.total || 0}` : (testMode ? 'Test Import to NetSuite' : 'Import to NetSuite')}
          </button>
        </div>
      )}

      {loading ? (
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading orders...</p>
        </div>
      ) : (
        <>
          <div className="orders-table-container">
            <table className="orders-table">
              <thead>
                <tr>
                  <th style={{ width: '40px' }}>
                    <input
                      type="checkbox"
                      checked={allEligibleSelected}
                      onChange={handleSelectAll}
                      title={`Select all eligible orders (${eligibleOrders.length})`}
                    />
                  </th>
                  <th
                    onClick={() => handleSort('order_number')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Order Numbers{getSortIcon('order_number')}
                  </th>
                  <th
                    onClick={() => handleSort('created_at')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Date{getSortIcon('created_at')}
                  </th>
                  <th
                    onClick={() => handleSort('customer_last_name')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Customer{getSortIcon('customer_last_name')}
                  </th>
                  <th
                    onClick={() => handleSort('email')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Email{getSortIcon('email')}
                  </th>
                  <th
                    onClick={() => handleSort('total_price')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Total{getSortIcon('total_price')}
                  </th>
                  <th
                    onClick={() => handleSort('financial_status')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Payment{getSortIcon('financial_status')}
                  </th>
                  <th
                    onClick={() => handleSort('fulfillment_status')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Fulfillment{getSortIcon('fulfillment_status')}
                  </th>
                  <th
                    onClick={() => handleSort('netsuite_sync_status')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    NetSuite{getSortIcon('netsuite_sync_status')}
                  </th>
                  <th
                    onClick={() => handleSort('tracking_numbers')}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Tracking{getSortIcon('tracking_numbers')}
                  </th>
                  <th>Items</th>
                </tr>
              </thead>
              <tbody>
                {orders.length === 0 ? (
                  <tr>
                    <td colSpan="11" className="no-data">
                      No orders found. Try syncing from Shopify or adjusting your filters.
                    </td>
                  </tr>
                ) : (
                  orders.map(order => {
                    const isExpanded = expandedOrders.has(order.id);
                    const lineItems = parseLineItems(order.line_items);
                    const shippingAddress = parseAddress(order.shipping_address);
                    const notInNetSuite = !order.netsuite_sync_status ||
                                         order.netsuite_sync_status === 'not_synced' ||
                                         order.netsuite_sync_status === 'pending';
                    const isEligible = notInNetSuite && order.financial_status === 'paid';
                    const isSelected = selectedOrders.has(order.shopify_order_id);

                    // Enhanced error detection
                    const hasError = order.has_errors || (order.import_error !== null && order.import_error !== '');
                    const requiresAttention = order.requires_attention;
                    const isDuplicate = order.is_duplicate;
                    const missingPayment = order.missing_payment;
                    const missingCustomer = order.missing_customer;

                    // Determine row styling based on issues
                    let rowClass = 'order-row';
                    if (isExpanded) rowClass += ' expanded';
                    if (isSelected) rowClass += ' selected';
                    if (isDuplicate) rowClass += ' duplicate-row';
                    else if (missingPayment) rowClass += ' missing-payment-row';
                    else if (hasError) rowClass += ' error-row';
                    else if (requiresAttention) rowClass += ' attention-row';

                    return (
                      <React.Fragment key={order.id}>
                        <tr
                          className={rowClass}
                          style={{ cursor: 'pointer' }}
                          title={order.attention_reason || ''}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => handleSelectOrder(order.shopify_order_id, isEligible)}
                              disabled={!isEligible}
                              title={!isEligible ? 'Order not eligible (must be paid and not in NetSuite)' : 'Select for import'}
                            />
                          </td>
                          <td className="order-number">
                            <span className="expand-icon" onClick={() => toggleOrderExpansion(order.id)} style={{ cursor: 'pointer' }}>
                              {isExpanded ? '▼' : '▶'}
                            </span>
                            <div style={{ display: 'inline-block' }}>
                              <a
                                href={`https://admin.shopify.com/store/${order.store_domain === 'candlefish1.myshopify.com' ? 'candlefish' : 'rewined-2022'}/orders/${order.shopify_order_id}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                onClick={(e) => e.stopPropagation()}
                                style={{ textDecoration: 'underline', color: '#007bff', marginRight: '4px' }}
                                title={`View in Shopify (${order.store_domain === 'candlefish1.myshopify.com' ? 'Candlefish' : 'Rewined'})`}
                              >
                                #{order.order_number}
                              </a>
                              {/* Issue indicators */}
                              {order.fraud_detected && (
                                <span className="issue-badge fraud-badge" title={order.fraud_reason || "Flagged as potential fraud"}>
                                  FRAUD?
                                </span>
                              )}
                              {isDuplicate && (
                                <span className="issue-badge duplicate-badge" title={`Duplicate of ${order.duplicate_of}`}>
                                  DUPLICATE
                                </span>
                              )}
                              {missingPayment && (
                                <span className="issue-badge payment-badge" title="Payment missing in NetSuite">
                                  NO PAYMENT
                                </span>
                              )}
                              {missingCustomer && (
                                <span className="issue-badge customer-badge" title="Customer email missing">
                                  NO CUSTOMER
                                </span>
                              )}
                              {hasError && !isDuplicate && !missingPayment && (
                                <span className="issue-badge error-badge" title={order.attention_reason || "Has errors"}>
                                  ERROR
                                </span>
                              )}
                              {order.netsuite_sales_order_number && (
                                <>
                                  <br />
                                  <a
                                    href={`https://${process.env.REACT_APP_NETSUITE_ACCOUNT_ID || '5359213'}.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${order.netsuite_sales_order_internal_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{ textDecoration: 'underline', color: '#28a745', fontSize: '0.9em' }}
                                  >
                                    {order.netsuite_sales_order_number}
                                  </a>
                                </>
                              )}
                            </div>
                          </td>
                          <td onClick={() => toggleOrderExpansion(order.id)}>{formatDate(order.created_at)}</td>
                          <td onClick={() => toggleOrderExpansion(order.id)}>
                            {order.customer_first_name || ''} {order.customer_last_name || ''}
                          </td>
                          <td onClick={() => toggleOrderExpansion(order.id)}>{order.email || '-'}</td>
                          <td className="total" onClick={() => toggleOrderExpansion(order.id)}>{formatCurrency(order.total_price)}</td>
                          <td onClick={() => toggleOrderExpansion(order.id)}>{getStatusBadge(order.financial_status, 'financial')}</td>
                          <td onClick={() => toggleOrderExpansion(order.id)}>{getStatusBadge(order.fulfillment_status_netsuite || order.fulfillment_status, 'fulfillment', order)}</td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {getNetSuiteBadge(order)}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {order.tracking_numbers ? (
                              <div style={{ fontSize: '0.85em' }}>
                                <div style={{ fontWeight: 'bold', color: '#007bff' }}>
                                  {order.tracking_numbers.split(',').map((tracking, idx) => (
                                    <div key={idx}>
                                      {order.carrier === 'UPS' ? (
                                        <a
                                          href={`https://www.ups.com/track?tracknum=${tracking.trim()}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: '#8B4513', textDecoration: 'underline' }}
                                        >
                                          {tracking.trim()}
                                        </a>
                                      ) : order.carrier === 'FedEx' ? (
                                        <a
                                          href={`https://www.fedex.com/fedextrack/?tracknumbers=${tracking.trim()}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: '#4B0082', textDecoration: 'underline' }}
                                        >
                                          {tracking.trim()}
                                        </a>
                                      ) : order.carrier === 'USPS' ? (
                                        <a
                                          href={`https://tools.usps.com/go/TrackConfirmAction?qtc_tLabels1=${tracking.trim()}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          style={{ color: '#004B87', textDecoration: 'underline' }}
                                        >
                                          {tracking.trim()}
                                        </a>
                                      ) : (
                                        <span>{tracking.trim()}</span>
                                      )}
                                    </div>
                                  ))}
                                </div>
                                {order.carrier && (
                                  <div style={{ color: '#666', fontSize: '0.9em', marginTop: '2px' }}>
                                    {order.carrier}
                                  </div>
                                )}
                              </div>
                            ) : order.fulfillment_status === 'fulfilled' ? (
                              <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.85em' }}>No tracking</span>
                            ) : (
                              <span style={{ color: '#ccc', fontSize: '0.85em' }}>-</span>
                            )}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {lineItems.length > 0 && (
                              <span className="item-count">{lineItems.length} items</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="expanded-content-row">
                            <td colSpan="11">
                              <div className="expanded-content">
                                <div className="expanded-sections">
                                  <div className="expanded-section">
                                    <h4>Line Items</h4>
                                    <table className="line-items-subtable">
                                      <thead>
                                        <tr>
                                          <th>Product</th>
                                          <th>SKU</th>
                                          <th>Qty</th>
                                          <th>Price</th>
                                          <th>Total</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {lineItems.map((item, idx) => (
                                          <tr key={idx}>
                                            <td>{item.name || item.title}</td>
                                            <td>{item.sku || '-'}</td>
                                            <td>{item.quantity}</td>
                                            <td>{formatCurrency(item.price)}</td>
                                            <td>{formatCurrency(item.price * item.quantity)}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>

                                  {shippingAddress && (
                                    <div className="expanded-section">
                                      <h4>Shipping Address</h4>
                                      <div className="address-block">
                                        {shippingAddress.name && <div>{shippingAddress.name}</div>}
                                        {shippingAddress.company && <div>{shippingAddress.company}</div>}
                                        {shippingAddress.address1 && <div>{shippingAddress.address1}</div>}
                                        {shippingAddress.address2 && <div>{shippingAddress.address2}</div>}
                                        <div>
                                          {shippingAddress.city}, {shippingAddress.province_code} {shippingAddress.zip}
                                        </div>
                                        {shippingAddress.country && <div>{shippingAddress.country}</div>}
                                        {shippingAddress.phone && <div>Phone: {shippingAddress.phone}</div>}
                                      </div>
                                    </div>
                                  )}

                                  {order.note && (
                                    <div className="expanded-section">
                                      <h4>Order Note</h4>
                                      <p>{order.note}</p>
                                    </div>
                                  )}

                                  {/* Show import tracking info if there's any import activity */}
                                  {(hasError || order.import_attempts > 0 || order.last_import_error || order.netsuite_sales_order_number) && (
                                    <div className={`expanded-section ${hasError ? 'error-section' : ''}`}>
                                      <h4>
                                        Import History
                                        {order.netsuite_sales_order_number && (
                                          <button
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleResetOrder(order);
                                            }}
                                            className="btn btn-sm btn-warning"
                                            style={{ marginLeft: '10px', fontSize: '0.8em', padding: '2px 8px' }}
                                          >
                                            🔄 Reset Order
                                          </button>
                                        )}
                                      </h4>
                                      <div className="error-details">
                                        <p><strong>Status:</strong> {order.import_status || 'pending'}</p>
                                        {order.import_attempts > 0 && (
                                          <p><strong>Attempts:</strong> {order.import_attempts}</p>
                                        )}
                                        {order.imported_at && (
                                          <p><strong>Imported At:</strong> {formatDate(order.imported_at)}</p>
                                        )}
                                        {order.import_attempted_at && (
                                          <p><strong>Last Attempt:</strong> {formatDate(order.import_attempted_at)}</p>
                                        )}
                                        {order.netsuite_sales_order_number && (
                                          <p>
                                            <strong>NetSuite Order:</strong>{' '}
                                            <a
                                              href={`https://${process.env.REACT_APP_NETSUITE_ACCOUNT_ID || '5359213'}.app.netsuite.com/app/accounting/transactions/salesord.nl?id=${order.netsuite_sales_order_internal_id}`}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              style={{ textDecoration: 'underline', color: '#28a745' }}
                                            >
                                              {order.netsuite_sales_order_number}
                                            </a>
                                          </p>
                                        )}
                                        {hasError && order.import_error && (
                                          <p style={{ color: '#e65100' }}><strong>Current Error:</strong> {order.import_error}</p>
                                        )}
                                        {!hasError && order.last_import_error && (
                                          <p style={{ color: '#666', fontStyle: 'italic' }}>
                                            <strong>Previous Error (Resolved):</strong> {order.last_import_error}
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}

                                  {/* Show fulfillment tracking */}
                                  {order.netsuite_sales_order_number && (
                                    <div className="expanded-section">
                                      <h4>Fulfillment & Tracking</h4>
                                      <div className="error-details">
                                        <p>
                                          <strong>Fulfillment Status:</strong>{' '}
                                          <span style={{
                                            color: order.fulfillment_status_netsuite === 'fulfilled' ? '#28a745' : '#666',
                                            fontWeight: 'bold'
                                          }}>
                                            {order.fulfillment_status_netsuite || 'pending'}
                                          </span>
                                        </p>
                                        {order.fulfillment_created_at && (
                                          <p><strong>Fulfilled At:</strong> {formatDate(order.fulfillment_created_at)}</p>
                                        )}

                                        {orderShipments[order.shopify_order_id] && orderShipments[order.shopify_order_id].length > 0 && (
                                          <div style={{ marginTop: '12px' }}>
                                            <strong>Shipments:</strong>
                                            {orderShipments[order.shopify_order_id].map((shipment, idx) => (
                                              <div key={idx} style={{
                                                marginTop: '8px',
                                                padding: '8px',
                                                backgroundColor: '#f5f5f5',
                                                borderRadius: '4px'
                                              }}>
                                                {shipment.netsuite_fulfillment_number && (
                                                  <p style={{ margin: '4px 0' }}>
                                                    <strong>IF#:</strong>{' '}
                                                    <a
                                                      href={`https://${process.env.REACT_APP_NETSUITE_ACCOUNT_ID || '5359213'}.app.netsuite.com/app/accounting/transactions/itemfulfillment.nl?id=${shipment.netsuite_fulfillment_id}`}
                                                      target="_blank"
                                                      rel="noopener noreferrer"
                                                      style={{ textDecoration: 'underline', color: '#28a745' }}
                                                    >
                                                      {shipment.netsuite_fulfillment_number}
                                                    </a>
                                                  </p>
                                                )}
                                                {shipment.tracking_number && (
                                                  <p style={{ margin: '4px 0' }}>
                                                    <strong>Tracking:</strong> {shipment.tracking_number}
                                                  </p>
                                                )}
                                                {shipment.carrier && (
                                                  <p style={{ margin: '4px 0' }}>
                                                    <strong>Carrier:</strong> {shipment.carrier}
                                                  </p>
                                                )}
                                                {shipment.shipped_date && (
                                                  <p style={{ margin: '4px 0' }}>
                                                    <strong>Shipped:</strong> {formatDate(shipment.shipped_date)}
                                                  </p>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        )}

                                        {orderShipments[order.shopify_order_id] && orderShipments[order.shopify_order_id].length === 0 && (
                                          <p style={{ marginTop: '8px', color: '#666', fontStyle: 'italic' }}>
                                            No shipments found
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange('prev')}
                disabled={currentPage === 1}
                className="btn btn-pagination"
              >
                Previous
              </button>
              <span className="page-info">
                Page {currentPage} of {totalPages} ({pagination.total} total orders)
              </span>
              <button
                onClick={() => handlePageChange('next')}
                disabled={currentPage === totalPages}
                className="btn btn-pagination"
              >
                Next
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShopifyOrderList;